import { FullCbbLogo } from "@/assets/AiBotAssets";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import OnBoardingCompanyLogo, { buildLogoUrl } from "./OnBoardingCompanyLogo";

type Props = {
  messages?: React.ReactNode[];
  subText?: string;
  inline?: boolean;
  domain?: string;
};

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** "my-brand.com" → "My Brand" */
function getTrainingSource(domain): string {
  if (!domain) return "";

  try {
    return new URL(
      domain.startsWith("http") ? domain : `https://${domain}`,
    ).host.replace(/^www\./, "");
  } catch {
    return domain
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .replace(/^www\./, "");
  }
}
const OnboardingFetchOverlay = ({
  messages,
  subText = "We are reading the site structure and preparing training sources for",
  inline = false,
  domain,
}: Props) => {
  const name = getTrainingSource(domain);
  const logoUrl = useMemo(() => buildLogoUrl(domain), [domain]);
  const [prefetchedLogoUrl, setPrefetchedLogoUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!logoUrl) {
      setPrefetchedLogoUrl(null);
      return;
    }

    let isCancelled = false;
    const image = new Image();

    image.onload = () => {
      if (!isCancelled) {
        setPrefetchedLogoUrl(logoUrl);
      }
    };

    image.onerror = () => {
      if (!isCancelled) {
        setPrefetchedLogoUrl(null);
      }
    };

    image.src = logoUrl;

    return () => {
      isCancelled = true;
    };
  }, [logoUrl]);

  const defaultMessages: React.ReactNode[] = [
    "Fetching your link...",
    "Grabbing brand details...",
    "Fishing out brand theme...",
    "Reading your site structure...",
    "Finding useful pages...",
    "Sorting fresh sources...",
    "Picking up brand cues...",
    "Tuning the agent context...",
    "Preparing training data...",
    "Almost there...",
  ];

  const resolvedMessages = messages ?? defaultMessages;

  const [messageIndex, setMessageIndex] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMessageIndex((currentIndex) =>
        currentIndex === resolvedMessages.length - 1 ? 0 : currentIndex + 1,
      );
    }, 2000);
    return () => window.clearInterval(interval);
  }, [resolvedMessages.length]);

  const currentMessage = resolvedMessages[messageIndex];

  return (
    <motion.div
      ref={overlayRef}
      className={`onboarding-fetch-overlay ${inline ? "inline-overlay" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-fetch-title"
      aria-describedby="onboarding-fetch-message"
      tabIndex={-1}
      initial={{ clipPath: "circle(0% at 50% 50%)", opacity: 0.96 }}
      animate={{ clipPath: "circle(150% at 50% 50%)", opacity: 1 }}
      exit={{ clipPath: "circle(0% at 50% 50%)", opacity: 0 }}
      transition={{ duration: 0.92, ease: [0.42, 0, 0.18, 1] }}
    >
      <div className="onboarding-fetch-liquid" aria-hidden="true">
        <div className="onboarding-fetch-blob onboarding-fetch-blob--1" />
        <div className="onboarding-fetch-blob onboarding-fetch-blob--2" />
      </div>

      {!inline && (
        <div className="onboarding-fetch-logo" aria-hidden="true">
          <FullCbbLogo />
        </div>
      )}

      <motion.div
        className="onboarding-fetch-content"
        initial={{ opacity: 0, y: 18, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.46, ease: [0.42, 0, 0.58, 1], delay: 0.32 }}
      >
        <div className="onboarding-fetch-copy flex flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.h2
              id="onboarding-fetch-title"
              key={messageIndex} // ← index, not string, since nodes aren't serializable
              className="onboarding-fetch-title"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              {currentMessage}
            </motion.h2>
          </AnimatePresence>
          <p
            id="onboarding-fetch-message"
            className="onboarding-fetch-detail flex w-max max-w-[unset] gap-[4px] !font-light"
          >
            <span className="sub-t w-max text-[16px] !font-light text-[#666]">
              {subText}
            </span>
            {prefetchedLogoUrl ? (
              <div className="flex h-[24px] w-[24px] items-center justify-center rounded-[4px] border border-gray-300 bg-white">
                <OnBoardingCompanyLogo domain={domain} />
              </div>
            ) : null}
            <span className="cm-name text-[16px] !font-medium text-[#444]">
              {name}
            </span>
          </p>
        </div>

        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {typeof currentMessage === "string" ? currentMessage : name}
        </span>
      </motion.div>
    </motion.div>
  );
};

export default OnboardingFetchOverlay;
